import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { useActiveEditor } from '@/store/editorContext';
import { PasteCleanup } from '@/editor/extensions/pasteCleanup';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  minHeight?: number;
  placeholder?: string;
}

export default function RichTextEditor({
  content,
  onChange,
  minHeight = 120,
}: RichTextEditorProps) {
  const { setActiveEditor } = useActiveEditor();

  const prevContentRef = useRef(content);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, HTMLAttributes: { class: 'text-link' } },
      }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      PasteCleanup,
    ],
    content,
    onUpdate: ({ editor: e }) => {
      prevContentRef.current = e.getHTML();
      onChange(e.getHTML());
    },
    onFocus: ({ editor }) => setActiveEditor(editor),
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none',
      },
    },
  });

  useEffect(() => {
    if (!editor || editor.isFocused) return;
    if (content !== prevContentRef.current) {
      prevContentRef.current = content;
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  return (
    <div
      className="rounded focus-within:outline focus-within:outline-2 focus-within:outline-primary focus-within:-outline-offset-2 [&_.ProseMirror]:outline-none [&_.ProseMirror]:p-2 [&_.ProseMirror_p]:my-0 [&_.ProseMirror_p]:mb-[0.5em] [&_.ProseMirror_h1]:text-[1.75em] [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:my-0 [&_.ProseMirror_h1]:mb-[0.5em] [&_.ProseMirror_h2]:text-[1.5em] [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:my-0 [&_.ProseMirror_h2]:mb-[0.5em] [&_.ProseMirror_h3]:text-[1.25em] [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:my-0 [&_.ProseMirror_h3]:mb-[0.5em] [&_.ProseMirror_ul]:pl-6 [&_.ProseMirror_ul]:my-0 [&_.ProseMirror_ul]:mb-[0.5em] [&_.ProseMirror_ol]:pl-6 [&_.ProseMirror_ol]:my-0 [&_.ProseMirror_ol]:mb-[0.5em] [&_.ProseMirror_blockquote]:border-l-[3px] [&_.ProseMirror_blockquote]:border-gray-300 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:ml-0 [&_.ProseMirror_blockquote]:italic [&_.ProseMirror_blockquote]:text-gray-500 [&_.ProseMirror_a]:text-primary [&_.ProseMirror_a]:underline [&_.ProseMirror_.text-link]:text-primary [&_.ProseMirror_.text-link]:underline"
      style={{ minHeight: minHeight - 16 }}
    >
      <EditorContent editor={editor} />
    </div>
  );
}
